<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import CloseButton from './CloseButton.svelte';
  import i18n from './i18n/i18n';
import VideoPlayIcon from './icons/VideoPlayIcon.svelte';
import VideoPauseIcon from './icons/VideoPauseIcon.svelte';
import VideoReplayIcon from './icons/VideoReplayIcon.svelte';
import VideoMuteIcon from './icons/VideoMuteIcon.svelte';
import VideoUnmuteIcon from './icons/VideoUnmuteIcon.svelte';
import { focusTrap } from "./utils.js";


  /* ____________ Mobile Detection ____________ */
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  export let hasDescription = false;
  export let content;
  export let textColor = '#ffffff';
  let videoUrl = content.videoAssets[0].url;


  const dispatch = createEventDispatcher();

  let closeButtonRef;
  let containerRef;
  let playerRef;
  let isPlaying = true;
  let isMuted = false;
  let progress = 0;
  let isHovering = false;
  let hasEnded = false;

  let videoDimensions = { width: 720, height: 1280, aspectRatio: 720/1280 };

  function calculateVideoDimensions() {
    if (!content?.videoAssets?.length) return;

    const maxScale = isMobile ? 0.85 : 0.7;
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 720;
    
    const originalWidth = content.videoAssets[0].width || 720;
    const originalHeight = content.videoAssets[0].height || 1280;
    const aspectRatio = originalWidth / originalHeight;

    const maxWidth = windowWidth * maxScale;
    const maxHeight = windowHeight * maxScale;

    let finalWidth = originalWidth;
    let finalHeight = originalHeight;

    if (finalWidth > maxWidth) {
      finalWidth = maxWidth;
      finalHeight = finalWidth / aspectRatio;
    }

    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = finalHeight * aspectRatio;
    }

    videoDimensions = { width: finalWidth, height: finalHeight, aspectRatio };
  }

  // Calculate dimensions when content changes
  $: if (content?.videoAssets?.length) {
    calculateVideoDimensions();
  }

  function handleClose() {
    dispatch('close');
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') handleClose();
  }

  function handlePlayPause() {
    if (hasEnded) {
      hasEnded = false;
      progress = 0;
      if (playerRef) {
        playerRef.currentTime = 0;
      }
    }
    isPlaying = !isPlaying;
    
    if (playerRef) {
      if (isPlaying) {
        playerRef.play().catch((error) => {
          console.error('[Video Play Error]', error);
          isPlaying = false;
        });
      } else {
        playerRef.pause();
      }
    }
  }

  function handleMuteUnmute() {
    isMuted = !isMuted;
    if (playerRef) {
      playerRef.muted = isMuted;
    }
  }

  function handleTimeUpdate() {
    console.log("handleTimeUpdate");
    if (playerRef) {
      const currentTime = playerRef.currentTime;
      const duration = playerRef.duration;
      progress = (currentTime / duration) * 100;
    }
  }

  function handleVideoClick(e) {
    e.stopPropagation();
    
    if (e.target.closest('.control-button')) {
      return;
    }

    handlePlayPause();
  }

  function handleEnded() {
    hasEnded = true;
    isPlaying = false;
  }

  function handleProgressBarClick(e) {
    e.stopPropagation();
    
    if (!playerRef) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    
    playerRef.currentTime = clickPosition * playerRef.duration;
    progress = clickPosition * 100;
    
    if (hasEnded) {
      hasEnded = false;
    }
    
    isPlaying = true;
    playerRef.play().catch((error) => {
      console.error('[Video Seek Error]', error);
      isPlaying = false;
    });
  }

  onMount(() => {
    window.addEventListener('resize', calculateVideoDimensions);
    return () => {
      window.removeEventListener('resize', calculateVideoDimensions);
    };
  });
</script>

<div
  class="popup-overlay" 
  on:click={handleClose}
  on:keydown={handleKeyDown}
  role="dialog"
  aria-modal="true"
  aria-label={`${content.platform} content: ${content.authorName || 'Social Media Post'}`}
>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="social-popup" on:click|stopPropagation>
    <div class="social-content">
      <main class="social-embed-container" bind:this={containerRef}>
        {#if content.videoAssets && content.videoAssets.length > 0}
          <div 
            class="video-container" 
            style="width: {videoDimensions.width}px; height: {videoDimensions.height}px"
            role="region"
            aria-label="Video player"
          >
            <div 
              class="video-wrapper"
              style="aspect-ratio: {videoDimensions.aspectRatio}"
              on:mouseenter={() => isHovering = true}
              on:mouseleave={() => isHovering = false}
              on:click={handleVideoClick}
              on:keydown={handleVideoClick}
              on:keyup={() => {}}
              role="application"
              aria-label="Video controls"
            >
              <video
                bind:this={playerRef}
                src={videoUrl}
                muted={isMuted}
                autoplay={true}
                on:timeupdate={handleTimeUpdate}
                on:ended={handleEnded}
                on:play={() => {
                  isPlaying = true;
                }}
                on:pause={() => {
                  isPlaying = false;
                }}
                on:waiting={() => {}}
                on:stalled={() => {}}
                on:error={(e) => {
                  console.error('[Video Error Event]', e);
                }}
                playsinline
                style="width: 100%; height: 100%;"
                aria-label="Video content"
              />
              {#if hasEnded}
                <div class="video-overlay" role="status" aria-label="Video ended">
                  <button 
                    class="replay-button"
                    on:click|stopPropagation={handlePlayPause}
                    aria-label="Replay video"
                  >
                    <VideoReplayIcon size={48} color={textColor} />
                  </button>
                </div>
              {/if}
              {#if !isPlaying && !hasEnded}
                <div class="video-overlay" role="status" aria-label="Video paused">
                  <button 
                    class="play-button"
                    on:click|stopPropagation={handlePlayPause}
                    aria-label="Play video"
                  >
                    <VideoPlayIcon size={48} color={textColor} />
                  </button>
                </div>
              {/if}
              <button 
                class="control-button mute-unmute {isMuted ? 'always-visible' : ''}"
                on:click|stopPropagation={handleMuteUnmute}
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                aria-pressed={isMuted}
              >
                {#if isMuted}
                  <VideoMuteIcon color={textColor} />
                {:else}
                  <VideoUnmuteIcon color={textColor} />
                {/if}
              </button>
              <div 
                class="video-controls"
                role="toolbar"
                aria-label="Video playback controls"
              >
                <button 
                  class="control-button play-pause"
                  on:click|stopPropagation={handlePlayPause}
                  aria-label={hasEnded ? 'Replay video' : (isPlaying ? 'Pause video' : 'Play video')}
                  aria-pressed={isPlaying}
                >
                  {#if hasEnded}
                    <VideoReplayIcon size={24} color={textColor} />
                  {:else if isPlaying}
                    <VideoPauseIcon size={24} color={textColor} />
                  {:else}
                    <VideoPlayIcon size={24} color={textColor} />
                  {/if}
                </button>
                <!-- svelte-ignore a11y-interactive-supports-focus -->
                <div 
                  class="progress-bar"
                  on:click|stopPropagation={handleProgressBarClick}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                  aria-valuetext={`${Math.round(progress)}% played`}
                  aria-label="Video progress"
                >
                  <div 
                    class="progress-fill"
                    style="width: {progress}%"
                    role="presentation"
                  />
                </div>
              </div>
            </div>
          </div>

          {#if content.description && hasDescription && !isMobile}
            <div 
              class="description" 
              style="width: {videoDimensions.width}px"
              role="complementary"
              aria-label="Video description"
            >
              {content.description}
            </div>
          {/if}
        {/if}
      </main>
      <div class="close-button-container">
        <CloseButton 
          bind:this={closeButtonRef}
          close={handleClose}
          color={textColor}
          useAbsolutePosition={false}
          marginBottom={isMobile ? '0' : '1rem'}
        />
      </div>
    </div>
  </div>
</div>

<style>
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    backdrop-filter: blur(2px);
  }

  .social-popup {
    background-color: transparent;
    width: auto;
    max-width: 95vw;
    max-height: 95vh;
    margin: auto;
    position: relative;
  }

  .social-content {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    max-height: 95vh;
    overflow: hidden;
  }

  .social-embed-container {
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    overflow: hidden;
  }

  .video-container {
    background-color: #000;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .video-wrapper {
    width: 100%;
    position: relative;
    cursor: pointer;
  }

  .video-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .video-wrapper:hover .video-controls {
    opacity: 1;
  }

  .control-button {
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .control-button.always-visible {
    opacity: 1;
  }

  .control-button:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  .progress-bar {
    position: absolute;
    bottom: 5px;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
  }

  .progress-fill {
    height: 100%;
    background: #ffffff;
    transform: translateZ(0);
    will-change: width;
    transition: width 0.5s linear;
    pointer-events: none;
  }

  .description {
    background-color: #ffffff;
    padding: 1.5rem;
    border-radius: 8px;
    font-size: 1rem;
    line-height: 1.5;
    white-space: pre-wrap;
    color: #333;
    flex-shrink: 0;
    max-height: min(20vh, calc(95vh - 100px));
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #000000 #ffffff;
    box-sizing: border-box;
    font-family: var(--font-body), sans-serif;
  }

  .close-button-container {
    flex-shrink: 0;
  }

  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .replay-button,
  .play-button {
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s ease;
    padding: 0;
  }

  .replay-button:hover,
  .play-button:hover {
    transform: scale(1.1);
    background: rgba(0, 0, 0, 0.7);
  }

  .replay-button:focus-visible,
  .play-button:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
    transform: scale(1.1);
  }

  .control-button.mute-unmute {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .video-wrapper:hover .control-button.mute-unmute {
    opacity: 1;
  }

  .control-button.mute-unmute.always-visible {
    opacity: 1;
  }

  @media (max-width: 768px) {
    .social-popup {
      width: 95vw;
      max-height: 85vh;
    }

    .social-content {
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      max-height: 85vh;
    }

    .close-button-container {
      padding-top: 0;
    }

    .description {
      display: none;
    }
  }

  /* Scrollbar styles */
  .description {
    scrollbar-width: thin;
    scrollbar-color: #000000 #ffffff;
  }

  :global(::-webkit-scrollbar) {
    width: 5px;
  }
  :global(::-webkit-scrollbar-thumb) {
    background: #000000;
    border-radius: 10px;
  }

  :global(::-webkit-scrollbar-thumb:hover) {
    background:#AAAAAA;
  }

  /* Add focus styles for keyboard navigation */
  .control-button:focus-visible,
  .progress-bar:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  .control-button:focus-visible {
    background: rgba(0, 0, 0, 0.7);
  }

  .progress-bar:focus-visible {
    height: 5px;
  }
</style> 